import { createAsyncThunk, createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  notificationsApi,
  NotificationRecipient,
  BulkUpdateItem,
  BulkUpdateResponse,
  NotificationStatistics,
} from "../../../../../api/notifications/notificationsApi";
import { RootState } from "../../../../../app/rootReducer";

interface NotificationSettingsState {
  recipients: NotificationRecipient[];
  statistics: NotificationStatistics | null;
  loading: boolean;
  updating: { [userId: string]: boolean };
  bulkUpdating: boolean;
  error: string | null;
  successMessage: string | null;
  filters: {
    search: string;
    activeOnly: boolean;
    notificationType: "all" | "all_calls" | "flagged_only";
  };
  selectedUserIds: string[];
}

const initialState: NotificationSettingsState = {
  recipients: [],
  statistics: null,
  loading: false,
  updating: {},
  bulkUpdating: false,
  error: null,
  successMessage: null,
  filters: {
    search: "",
    activeOnly: false,
    notificationType: "all",
  },
  selectedUserIds: [],
};

// Async thunks
export const fetchRecipients = createAsyncThunk(
  "notificationSettings/fetchRecipients",
  async (activeOnly: boolean = false) => {
    const recipients = await notificationsApi.listRecipients(activeOnly);
    return recipients;
  }
);

export const updateRecipient = createAsyncThunk(
  "notificationSettings/updateRecipient",
  async ({
    userId,
    notificationType,
    isActive,
  }: {
    userId: string;
    notificationType: "all_calls" | "flagged_only";
    isActive: boolean;
  }) => {
    const updated = await notificationsApi.updateRecipient(userId, {
      userId,
      notificationType,
      isActive,
    });
    return updated;
  }
);

export const bulkUpdateRecipients = createAsyncThunk(
  "notificationSettings/bulkUpdateRecipients",
  async (updates: BulkUpdateItem[]) => {
    const response = await notificationsApi.bulkUpdateRecipients(updates);
    return response;
  }
);

export const fetchStatistics = createAsyncThunk(
  "notificationSettings/fetchStatistics",
  async () => {
    const stats = await notificationsApi.getStatistics();
    return stats;
  }
);

const notificationSettingsSlice = createSlice({
  name: "notificationSettings",
  initialState,
  reducers: {
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },
    setActiveOnly: (state, action: PayloadAction<boolean>) => {
      state.filters.activeOnly = action.payload;
    },
    setNotificationTypeFilter: (
      state,
      action: PayloadAction<"all" | "all_calls" | "flagged_only">
    ) => {
      state.filters.notificationType = action.payload;
    },
    toggleUserSelection: (state, action: PayloadAction<string>) => {
      const userId = action.payload;
      const index = state.selectedUserIds.indexOf(userId);
      if (index >= 0) {
        state.selectedUserIds.splice(index, 1);
      } else {
        state.selectedUserIds.push(userId);
      }
    },
    selectAllUsers: (state) => {
      state.selectedUserIds = state.recipients
        .filter((r) => {
          // Apply current filters
          if (state.filters.activeOnly && !r.isActive) return false;
          if (
            state.filters.notificationType !== "all" &&
            r.notificationType !== state.filters.notificationType
          )
            return false;
          if (
            state.filters.search &&
            !r.userName?.toLowerCase().includes(state.filters.search.toLowerCase()) &&
            !r.userEmail?.toLowerCase().includes(state.filters.search.toLowerCase())
          )
            return false;
          return true;
        })
        .map((r) => r.userId);
    },
    clearSelection: (state) => {
      state.selectedUserIds = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch recipients
    builder
      .addCase(fetchRecipients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.loading = false;
        state.recipients = action.payload;
      })
      .addCase(fetchRecipients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch recipients";
      });

    // Update single recipient with optimistic updates
    builder
      .addCase(updateRecipient.pending, (state, action) => {
        state.updating[action.meta.arg.userId] = true;
        state.error = null;
        
        // Optimistic update - immediately update the UI
        const { userId, notificationType, isActive } = action.meta.arg;
        const index = state.recipients.findIndex((r) => r.userId === userId);
        if (index >= 0) {
          // Store the previous state in case we need to rollback
          const previousState = { ...state.recipients[index] };
          
          // Apply optimistic update
          state.recipients[index] = {
            ...state.recipients[index],
            notificationType,
            isActive,
            updatedAt: new Date().toISOString(),
          };
          
          // Store previous state for potential rollback
          (state as any)._optimisticRollback = { userId, previousState };
        }
      })
      .addCase(updateRecipient.fulfilled, (state, action) => {
        const updated = action.payload;
        state.updating[updated.userId] = false;
        
        // Replace optimistic update with server response
        const index = state.recipients.findIndex((r) => r.userId === updated.userId);
        if (index >= 0) {
          state.recipients[index] = updated;
        }
        
        // Clear rollback data
        delete (state as any)._optimisticRollback;
        
        state.successMessage = `Updated preferences for ${updated.userName || updated.userEmail}`;
      })
      .addCase(updateRecipient.rejected, (state, action) => {
        state.updating[action.meta.arg.userId] = false;
        state.error = action.error.message || "Failed to update recipient";
        
        // Rollback optimistic update on failure
        const rollbackData = (state as any)._optimisticRollback;
        if (rollbackData && rollbackData.userId === action.meta.arg.userId) {
          const index = state.recipients.findIndex((r) => r.userId === rollbackData.userId);
          if (index >= 0) {
            state.recipients[index] = rollbackData.previousState;
          }
          delete (state as any)._optimisticRollback;
        }
      });

    // Bulk update
    builder
      .addCase(bulkUpdateRecipients.pending, (state) => {
        state.bulkUpdating = true;
        state.error = null;
      })
      .addCase(bulkUpdateRecipients.fulfilled, (state, action) => {
        state.bulkUpdating = false;
        const { updated, failed } = action.payload;
        state.successMessage = `Successfully updated ${updated} recipients`;
        if (failed > 0) {
          state.successMessage += `, ${failed} failed`;
        }
        state.selectedUserIds = [];
      })
      .addCase(bulkUpdateRecipients.rejected, (state, action) => {
        state.bulkUpdating = false;
        state.error = action.error.message || "Failed to bulk update recipients";
      });

    // Fetch statistics
    builder
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      });
  },
});

export const {
  setSearch,
  setActiveOnly,
  setNotificationTypeFilter,
  toggleUserSelection,
  selectAllUsers,
  clearSelection,
  clearError,
  clearSuccessMessage,
} = notificationSettingsSlice.actions;

// Selectors

/**
 * Base selectors for notification settings state
 */
const selectNotificationSettings = (state: RootState) => state.notificationSettings;
const selectRecipients = (state: RootState) => state.notificationSettings.recipients;
const selectFilters = (state: RootState) => state.notificationSettings.filters;
const selectSelectedUserIds = (state: RootState) => state.notificationSettings.selectedUserIds;

/**
 * Memoized selector that filters recipients based on current filter criteria.
 * Recomputes only when recipients or filters change.
 */
export const selectFilteredRecipients = createSelector(
  [selectRecipients, selectFilters],
  (recipients, filters) => {
    return recipients.filter((recipient) => {
      // Active filter
      if (filters.activeOnly && !recipient.isActive) return false;

      // Notification type filter
      if (
        filters.notificationType !== "all" &&
        recipient.notificationType !== filters.notificationType
      ) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          recipient.userName?.toLowerCase().includes(searchLower) ||
          recipient.userEmail?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }
);

/**
 * Factory selector to check if a specific user is selected.
 * Creates a memoized selector for each userId.
 */
export const selectIsUserSelected = (userId: string) =>
  createSelector(
    [selectSelectedUserIds],
    (selectedUserIds) => selectedUserIds.includes(userId)
  );

/**
 * Memoized selector for the count of selected users
 */
export const selectSelectedCount = createSelector(
  [selectSelectedUserIds],
  (selectedUserIds) => selectedUserIds.length
);

/**
 * Memoized selector to get statistics with derived data
 */
export const selectStatisticsWithPercentages = createSelector(
  [(state: RootState) => state.notificationSettings.statistics],
  (statistics) => {
    if (!statistics) return null;
    
    return {
      ...statistics,
      activePercentage: statistics.total > 0 
        ? Math.round((statistics.active / statistics.total) * 100) 
        : 0,
      allCallsPercentage: statistics.active > 0
        ? Math.round((statistics.all_calls / statistics.active) * 100)
        : 0,
      flaggedOnlyPercentage: statistics.active > 0
        ? Math.round((statistics.flagged_only / statistics.active) * 100)
        : 0,
    };
  }
);

export default notificationSettingsSlice.reducer;